import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Option, ThemeIcon, RoundToggle } from '@umanforge/common';
import SelectInput, { GroupedOption as OldGroupedOption } from '@common_components/inputs/selects/selectInput.component';

import { Theme } from 'interfaces/theme.interface';

interface SafetyCrossSettingsProps {
  themesOption: Option<Theme>[];
  createEventOnWarning: boolean;
  setCreateEventOnWarning: ( value: boolean ) => void;
  createEventOnError: boolean;
  setCreateEventOnError: ( value: boolean ) => void;
  safetyCrossThemeWarningId: string | null;
  setSafetyCrossThemeWarningId: ( value: string | null ) => void;
  safetyCrossThemeErrorId: string | null;
  setSafetyCrossThemeErrorId: ( value: string | null ) => void;
}

const SafetyCrossSettings: React.FC<SafetyCrossSettingsProps> = ( {
  themesOption,
  createEventOnWarning,
  setCreateEventOnWarning,
  createEventOnError,
  setCreateEventOnError,
  safetyCrossThemeWarningId,
  setSafetyCrossThemeWarningId,
  safetyCrossThemeErrorId,
  setSafetyCrossThemeErrorId
} ) => {
  const { t } = useTranslation();
  const themesRefreshTrigger = themesOption.map( theme => { return theme.object.id; } ).join( ',' );


  useEffect( () => {
    if ( !createEventOnWarning ) {
      return;
    }

    if ( safetyCrossThemeWarningId === null && themesOption.length > 0 ) {
      setSafetyCrossThemeWarningId( themesOption[ 0 ].object.id );
    }
  }, [ createEventOnWarning, themesOption, safetyCrossThemeWarningId, setSafetyCrossThemeWarningId ] );

  useEffect( () => {
    if ( !createEventOnError ) {
      return;
    }

    if ( safetyCrossThemeErrorId === null && themesOption.length > 0 ) {
      setSafetyCrossThemeErrorId( themesOption[ 0 ].object.id );
    }
  }, [ createEventOnError, themesOption, safetyCrossThemeErrorId, setSafetyCrossThemeErrorId ] );
  const fetchSafetyCrossThemeOptions = (): Promise<OldGroupedOption[]> => {
    const options = themesOption.map( theme => { return { id: theme.object.id, label: theme.label }; } );
    return Promise.resolve( [ { options } ] );
  };

  return (
    <>
      <div className='flex flex-col gap-4'>
        {/* Desktop / large layout: both toggles on one row, both selects on one row */}
        <div className='hidden md:flex flex-col gap-2 pt-2'>
          <div className='grid grid-cols-2 gap-4'>
            <RoundToggle
              label={ t( 'translation:create_event_on_warning' ) }
              checked={ createEventOnWarning }
              onChange={ setCreateEventOnWarning }
            />
            <RoundToggle
              label={ t( 'translation:create_event_on_error' ) }
              checked={ createEventOnError }
              onChange={ setCreateEventOnError }
            />
          </div>

          <div className='grid grid-cols-2 gap-4 mt-2'>
            <div className={ createEventOnWarning ? '' : 'invisible pointer-events-none' }>
              <SelectInput
                icon={ <ThemeIcon className='h-7 w-7 text-gray-base-dark' /> }
                label={ t( 'translation:safety_cross_theme_warning' ) }
                fetchOptions={ fetchSafetyCrossThemeOptions }
                refreshTrigger={ themesRefreshTrigger }
                labelPosition='top'
                selected={ safetyCrossThemeWarningId ?? '' }
                onSelect={ value => { setSafetyCrossThemeWarningId( value || null ); } }
              />
            </div>

            <div className={ createEventOnError ? '' : 'invisible pointer-events-none' }>
              <SelectInput
                icon={ <ThemeIcon className='h-7 w-7 text-gray-base-dark' /> }
                label={ t( 'translation:safety_cross_theme_error' ) }
                fetchOptions={ fetchSafetyCrossThemeOptions }
                refreshTrigger={ themesRefreshTrigger }
                labelPosition='top'
                selected={ safetyCrossThemeErrorId ?? '' }
                onSelect={ value => { setSafetyCrossThemeErrorId( value || null ); } }
              />
            </div>
          </div>
        </div>

        <div className='flex flex-col gap-3 pt-2 md:hidden'>
          <div className='flex flex-col gap-2'>
            <RoundToggle
              label={ t( 'translation:create_event_on_warning' ) }
              checked={ createEventOnWarning }
              onChange={ setCreateEventOnWarning }
            />

            { createEventOnWarning && (
              <div className='mt-1'>
                <SelectInput
                  icon={ <ThemeIcon className='h-7 w-7 text-gray-base-dark' /> }
                  label={ t( 'translation:safety_cross_theme_warning' ) }
                  fetchOptions={ fetchSafetyCrossThemeOptions }
                  refreshTrigger={ themesRefreshTrigger }
                  labelPosition='top'
                  selected={ safetyCrossThemeWarningId ?? '' }
                  onSelect={ value => { setSafetyCrossThemeWarningId( value || null ); } }
                />
              </div>
            ) }
          </div>

          <div className='flex flex-col gap-2'>
            <RoundToggle
              label={ t( 'translation:create_event_on_error' ) }
              checked={ createEventOnError }
              onChange={ setCreateEventOnError }
            />

            { createEventOnError && (
              <div className='mt-1'>
                <SelectInput
                  icon={ <ThemeIcon className='h-7 w-7 text-gray-base-dark' /> }
                  label={ t( 'translation:safety_cross_theme_error' ) }
                  fetchOptions={ fetchSafetyCrossThemeOptions }
                  refreshTrigger={ themesRefreshTrigger }
                  labelPosition='top'
                  selected={ safetyCrossThemeErrorId ?? '' }
                  onSelect={ value => { setSafetyCrossThemeErrorId( value || null ); } }
                />
              </div>
            ) }
          </div>
        </div>
      </div>
    </>
  );
};

export default SafetyCrossSettings;
